import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { MapEmptyState } from "./map-empty-state.component";
import { MapErrorBoundary } from "./map-error-boundary.component";
import { MapPlaceholder } from "./map-placeholder.component";
import { MapSkeleton } from "./map-skeleton.component";
import type {
	MapCenter,
	MapNavigationControlPosition,
	PharmacyMarker,
} from "./pharmacy-map.types";

const PharmacyMap = lazy(() => import("./pharmacy-map.component"));

interface PharmacyMapLazyProps {
	center: MapCenter | null;
	pharmacyMarkers?: PharmacyMarker[];
	selectedPharmacyId?: number | null;
	onPharmacyMarkerClick?: (pharmacyId: number) => void;
	onDismissPreview?: () => void;
	zoom?: number;
	interactive?: boolean;
	navigationControlPosition?: MapNavigationControlPosition;
	isLoading?: boolean;
	hasVendorResults?: boolean;
}

/**
 * Lazy-loaded wrapper for PharmacyMap.
 * Handles SSR boundary, loading, no-postal-code placeholder, and render
 * errors. One MapLibre instance stays mounted; an opaque skeleton covers
 * the canvas until the first idle (tiles painted), so first open does
 * not flash the style's empty land colour.
 */
export function PharmacyMapLazy({
	center,
	pharmacyMarkers,
	selectedPharmacyId,
	onPharmacyMarkerClick,
	onDismissPreview,
	zoom,
	interactive,
	navigationControlPosition,
	isLoading = false,
	hasVendorResults = true,
}: PharmacyMapLazyProps) {
	const [isClient, setIsClient] = useState(false);
	const [isMapReady, setIsMapReady] = useState(false);
	const handleMapReady = useCallback(() => setIsMapReady(true), []);

	useEffect(() => {
		setIsClient(true);
	}, []);

	useEffect(() => {
		if (center == null) {
			setIsMapReady(false);
		}
	}, [center]);

	if (!isClient) {
		return <MapSkeleton />;
	}

	if (!center) {
		return isLoading ? <MapSkeleton /> : <MapPlaceholder />;
	}

	const hasMarkers = (pharmacyMarkers?.length ?? 0) > 0;
	const showEmptyOverlay =
		!hasMarkers && !hasVendorResults && !isLoading && isMapReady;
	const showSkeletonOverlay = !isMapReady;

	return (
		<MapErrorBoundary>
			<div className="relative w-full h-full">
				<Suspense fallback={<MapSkeleton />}>
					<PharmacyMap
						center={center}
						pharmacyMarkers={pharmacyMarkers}
						selectedPharmacyId={selectedPharmacyId}
						onPharmacyMarkerClick={onPharmacyMarkerClick}
						onDismissPreview={onDismissPreview}
						zoom={zoom}
						interactive={interactive}
						navigationControlPosition={navigationControlPosition}
						onReady={handleMapReady}
					/>
				</Suspense>
				{showSkeletonOverlay ? (
					<div className="absolute inset-0 flex items-center justify-center bg-white">
						<MapSkeleton />
					</div>
				) : null}
				{showEmptyOverlay ? (
					<div className="absolute inset-0">
						<MapEmptyState />
					</div>
				) : null}
			</div>
		</MapErrorBoundary>
	);
}
